import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Modal from "../../ui/UIElements/Modal";
import { Icon } from "../../ui/UIElements/Icon";
import { cn } from "@/lib/utils";
import { useMetaAdsInsightsDaily } from "../../../hooks/metaAds/useMetaAdsInsightsDaily";
import {
  META_ADS_DATE_PRESET_OPTIONS,
  formatMetaAdsMoneyFromCents,
} from "./metaAdsUtils";
import { metaAdsFormSectionClass } from "./metaAdsTheme";

function formatShortDay(isoDate) {
  if (!isoDate) {
    return "";
  }
  const d = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) {
    return String(isoDate);
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatCpcUsd(v) {
  if (v == null || Number.isNaN(Number(v))) {
    return "—";
  }
  const cents = Math.round(Number(v) * 100);
  return formatMetaAdsMoneyFromCents(cents);
}

function formatCtrPct(v) {
  if (v == null || Number.isNaN(Number(v))) {
    return "—";
  }
  const n = Number(v);
  const pct = n <= 1 && n >= 0 ? n * 100 : n;
  return `${pct.toFixed(2)}%`;
}

const MetaAdsInsightsModal = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName,
  datePreset,
}) => {
  const { t } = useTranslation("metaAds");
  const { data, isLoading, isError, error } = useMetaAdsInsightsDaily({
    entityType,
    entityId,
    datePreset,
    enabled: isOpen,
  });

  const rangeLabel = useMemo(() => {
    const opt = META_ADS_DATE_PRESET_OPTIONS.find(
      (o) => o.value === datePreset,
    );
    return opt ? t(`performance.${opt.i18nKey}`) : String(datePreset || "");
  }, [datePreset, t]);

  const chartData = useMemo(() => {
    const series = data?.series ?? [];
    return series.map((row) => ({
      ...row,
      dayLabel: formatShortDay(row.date),
      spendNum: Number(row.spend) || 0,
      clicksNum: Number(row.clicks) || 0,
    }));
  }, [data?.series]);

  const displayName =
    entityName ||
    data?.objectId ||
    entityId ||
    t("performance.insightsUntitled");

  const subtitle = t("performance.insightsModalSubtitle", {
    name: displayName,
    range: rangeLabel,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("performance.insightsModalTitle")}
      subtitle={subtitle}
      maxWidth="max-w-4xl"
      className="border-gray-800 bg-gray-900"
    >
      <div className="space-y-6">
        <p className="text-sm text-gray-400">
          {t("performance.insightsSpendVsClicksHint")}
        </p>

        <div
          className={cn(
            metaAdsFormSectionClass,
            "relative min-h-[280px] border-gray-800 bg-gray-950/80 sm:min-h-[320px]",
          )}
        >
          {isLoading ? (
            <div
              className="flex min-h-[260px] flex-col items-center justify-center gap-3 sm:min-h-[300px]"
              role="status"
              aria-live="polite"
              aria-label={t("performance.insightsLoading")}
            >
              <Icon
                name="Loader2"
                className="h-8 w-8 shrink-0 animate-spin text-sky-400/90"
                aria-hidden
              />
              <span className="text-sm text-gray-400">
                {t("performance.insightsLoading")}
              </span>
            </div>
          ) : null}

          {!isLoading && isError ? (
            <div
              className="flex min-h-[260px] flex-col items-center justify-center gap-2 px-4 text-center sm:min-h-[300px]"
              role="alert"
            >
              <Icon
                name="AlertCircle"
                className="h-10 w-10 text-amber-400/90"
                aria-hidden
              />
              <p className="text-sm text-gray-200">
                {t("performance.insightsError")}
              </p>
              <p className="text-xs text-gray-500">
                {error?.message
                  ? String(error.message)
                  : t("performance.error")}
              </p>
            </div>
          ) : null}

          {!isLoading && !isError && chartData.length === 0 ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center gap-2 px-4 text-center sm:min-h-[300px]">
              <Icon
                name="BarChart3"
                className="h-10 w-10 text-gray-600"
                aria-hidden
              />
              <p className="text-sm text-gray-300">
                {t("performance.insightsEmpty")}
              </p>
            </div>
          ) : null}

          {!isLoading && !isError && chartData.length > 0 ? (
            <div className="h-[280px] w-full min-w-0 sm:h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    stroke="rgba(75,85,99,0.35)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="dayLabel"
                    tick={{ fill: "#9ca3af", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "rgba(75,85,99,0.5)" }}
                  />
                  <YAxis
                    yAxisId="spend"
                    tick={{ fill: "#7dd3fc", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) =>
                      formatMetaAdsMoneyFromCents(Math.round(Number(v) * 100))
                    }
                    width={72}
                  />
                  <YAxis
                    yAxisId="clicks"
                    orientation="right"
                    tick={{ fill: "#86efac", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={40}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) {
                        return null;
                      }
                      const p = payload[0]?.payload;
                      if (!p) {
                        return null;
                      }
                      return (
                        <div className="rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 shadow-xl">
                          <p className="text-xs font-medium text-white">
                            {p.date || label}
                          </p>
                          <p className="mt-1 text-xs text-sky-200/95">
                            {t("performance.insightsTooltipSpend")}:{" "}
                            {formatMetaAdsMoneyFromCents(
                              Math.round((Number(p.spend) || 0) * 100),
                            )}
                          </p>
                          <p className="text-xs text-emerald-200/95">
                            {t("performance.insightsTooltipClicks")}:{" "}
                            {p.clicks ?? "—"}
                          </p>
                          <p className="text-xs text-gray-400">
                            {t("performance.insightsTooltipImpressions")}:{" "}
                            {p.impressions ?? "—"}
                          </p>
                          <p className="text-xs text-gray-400">
                            {t("performance.insightsTooltipCpc")}:{" "}
                            {formatCpcUsd(p.cpc)}
                          </p>
                          <p className="text-xs text-gray-400">
                            {t("performance.insightsTooltipCtr")}:{" "}
                            {formatCtrPct(p.ctr)}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: 12,
                      paddingTop: 12,
                      color: "#d1d5db",
                    }}
                  />
                  <Line
                    yAxisId="spend"
                    type="monotone"
                    dataKey="spendNum"
                    name={t("performance.insightsLegendSpend")}
                    stroke="#38bdf8"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#0ea5e9" }}
                  />
                  <Line
                    yAxisId="clicks"
                    type="monotone"
                    dataKey="clicksNum"
                    name={t("performance.insightsLegendClicks")}
                    stroke="#4ade80"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: "#22c55e" }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default MetaAdsInsightsModal;
